/*!
 * edges.js — the score's connection graph, as data
 * © 2025 Rob Canning — GPLv3
 *
 * An edge is one element driving another: a follow() binding, or a trig:<uid>
 * object trigger. Edges are built from parsed cue ASTs while assignCues walks
 * the tree, which means they exist whether or not anything is playing.
 *
 * WHY THAT MATTERS
 * The obvious place to read the graph from is the ParamBus subscriber map. It
 * is the wrong place twice over. Bindings are created when a thing STARTS —
 * bindParam runs inside startSynthVoice — so a stopped score has almost no
 * subscriptions at all, and in a scrolling score only the few cues near the
 * playhead are ever live, so the runtime graph is a keyhole onto perhaps a
 * twentieth of the patch. A connection is a property of the score, not of
 * playback, and this is where that property lives.
 *
 * Edge shape:
 *   {
 *     kind: "signal" | "trigger",
 *     from: { uid, channel } | { address },   // address ⇒ incoming OSC
 *     to:   { uid, param },                   // param is "" for a trigger
 *     map:  { range, curve, default } | null, // signal edges only
 *     el, cueExpr,
 *   }
 *
 * Nothing here dispatches anything. The ParamBus still moves the values; this
 * is the description of what is wired to what, for validation to check and for
 * the patch view to draw.
 */

import { onReset } from "../system/teardown.js";

let _edges = [];

const sameEndpoint = (a, b) =>
  a?.uid === b?.uid && a?.channel === b?.channel && a?.address === b?.address && a?.param === b?.param;

/**
 * Open a scan. Like refs.js this prunes rather than clears, because assignCues
 * runs again per page in a page-mode score — resetting would leave the graph
 * describing only whichever tree was assigned last.
 */
export function beginEdgeScan() {
  _edges = _edges.filter((e) => !e.el || e.el.isConnected);
}

/**
 * Drop every edge belonging to one element.
 *
 * Pruning by isConnected is not enough when an element's DSL is REWRITTEN in
 * place — editing a cue keeps the same node, so a follow() that was just deleted
 * would survive the rescan and keep its cord on screen until a reload. Anyone
 * who rewrites an element's DSL calls this first: its old edges describe text
 * that no longer exists.
 */
export function dropEdgesFor(el) {
  if (!el) return 0;
  const before = _edges.length;
  _edges = _edges.filter((e) => e.el !== el);
  return before - _edges.length;
}

/** Forget everything. For a project change. */
export function resetEdges() {
  _edges = [];
}
onReset("project", resetEdges);

/** Add an edge, replacing an identical one from a previous scan of the same tree. */
export function addEdge(edge) {
  if (!edge?.from || !edge?.to) return null;
  for (let i = 0; i < _edges.length; i++) {
    const e = _edges[i];
    if (e.kind === edge.kind && e.el === edge.el && sameEndpoint(e.from, edge.from) && sameEndpoint(e.to, edge.to)) {
      _edges[i] = edge;
      return edge;
    }
  }
  _edges.push(edge);
  return edge;
}

/**
 * Record a follow() binding as an edge.
 * @param {object} ref     signalRef or oscRef, as parsed from follow()
 * @param {string} param   the parameter it drives (freq, sx, dur…)
 * @param {string} toUid   uid of the cue being driven
 */
export function addSignalEdge(ref, param, toUid, el, cueExpr) {
  if (!ref) return null;
  const from = ref.type === "oscRef"
    ? { address: ref.address }
    : { uid: ref.source, channel: ref.channel };
  const map = (ref.range || ref.curve || ref.default !== undefined)
    ? { range: ref.range ?? null, curve: ref.curve ?? null, default: ref.default ?? null }
    : null;
  return addEdge({ kind: "signal", from, to: { uid: toUid || "", param }, map, el, cueExpr });
}

/** Record a trig:<uid> object trigger as an edge. */
export function addTriggerEdge(sourceUid, toUid, el, cueExpr) {
  if (!sourceUid) return null;
  return addEdge({
    kind: "trigger",
    from: { uid: sourceUid },
    to: { uid: toUid || "", param: "" },
    map: null, el, cueExpr,
  });
}

/**
 * Record a trig:"adc.onset>0.5" threshold trigger as an edge.
 *
 * Drawn like an object trigger, but the source carries a channel, so the patch
 * view can show WHICH measurement fires the cue rather than just "adc".
 */
export function addSignalTriggerEdge(source, channel, toUid, el, cueExpr) {
  if (!source) return null;
  return addEdge({
    kind: "trigger",
    from: { uid: source, channel: channel || "" },
    to: { uid: toUid || "", param: "" },
    map: null, el, cueExpr,
  });
}

/** Every edge in the current score. */
export function getEdges() {
  return _edges.slice();
}

/** Edges leaving a uid (what this element drives). */
export function edgesFrom(uid) {
  return _edges.filter((e) => e.from.uid === uid);
}

/** Edges arriving at a uid (what drives this element). */
export function edgesTo(uid) {
  return _edges.filter((e) => e.to.uid === uid);
}

/** A one-line-per-edge summary, for the console. */
export function describeEdges() {
  return _edges.map((e) => {
    // A trigger edge has a source uid but no channel — it is the whole object
    // that fires, not one of its signals.
    const from = e.from.address ? `osc ${e.from.address}`
      : e.from.channel ? `${e.from.uid}.${e.from.channel}`
      : e.from.uid;
    const to = e.to.param ? `${e.to.uid}.${e.to.param}` : e.to.uid;
    const range = e.map?.range ? ` [${e.map.range.join("→")}]` : "";
    const curve = e.map?.curve ? ` ${e.map.curve}` : "";
    return `${e.kind === "trigger" ? "▸" : "~"} ${from} → ${to}${range}${curve}`;
  });
}

window.oscillaEdges = { getEdges, edgesFrom, edgesTo, describeEdges, resetEdges };
